import { useState, useEffect, useRef } from 'react';

const clients = [
  {
    name: 'World Food Programme',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/WFP_logo_%282022%29.svg/320px-WFP_logo_%282022%29.svg.png',
    project: 'Cash-Based Transfers Evaluation',
    description: 'Evaluation of effects and cost-benefit analysis of the GFD Cash Modality scale-up for refugees and host communities.',
    tag: 'Research & Evaluation',
    color: 'bg-sky-50 text-sky-700',
    tagActive: 'bg-white/20 text-white',
    fallbackIcon: 'ri-global-line',
  },
  {
    name: 'IFAD',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b3/IFAD_logo.svg/320px-IFAD_logo.svg.png',
    project: 'Smallholder Horticulture Marketing Programme',
    description: 'Impact evaluation of ShoMaP involving data collection and analysis across seven counties in Kenya.',
    tag: 'Data Collection & Analysis',
    color: 'bg-green-50 text-green-700',
    tagActive: 'bg-white/20 text-white',
    fallbackIcon: 'ri-global-line',
  },
  {
    name: 'NSSF Uganda',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/NSSF_Uganda_logo.svg/320px-NSSF_Uganda_logo.svg.png',
    project: 'Oracle ERP Upgrade & GIS Mapping',
    description: 'Implemented training and capacity building, upgrade of Oracle JD Edwards Enterprise One, and mapping of customer locations.',
    tag: 'IT Services & GIS',
    color: 'bg-teal-50 text-teal-700',
    tagActive: 'bg-white/20 text-white',
    fallbackIcon: 'ri-building-2-line',
  },
  {
    name: 'Mercy Corps',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Mercy_Corps_logo.svg/320px-Mercy_Corps_logo.svg.png',
    project: 'PRIME Project Capacity Building',
    description: 'Capacity building for the $52M USAID-funded PRIME project — improving knowledge management and spatial analytics in Ethiopia.',
    tag: 'Training & Capacity Building',
    color: 'bg-amber-50 text-amber-700',
    tagActive: 'bg-white/20 text-white',
    fallbackIcon: 'ri-global-line',
  },
  {
    name: 'FIPS-Africa',
    logo: 'https://readdy.ai/api/search-image?query=FIPS-Africa%20farm%20input%20promotion%20sub-saharan%20africa%20organization%20logo%20simple%20clean%20white%20background%20green%20agriculture&width=200&height=80&seq=fips-africa-logo-001&orientation=landscape',
    project: 'GIS & Cloud Services',
    description: 'GIS services, ODK implementation, Google App Engine and MySQL database solutions for farm input promotion across sub-Saharan Africa.',
    tag: 'GIS & Cloud',
    color: 'bg-emerald-50 text-emerald-700',
    tagActive: 'bg-white/20 text-white',
    fallbackIcon: 'ri-seedling-line',
  },
  {
    name: 'Enabel (BTC)',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Enabel_logo.svg/320px-Enabel_logo.svg.png',
    project: 'Biodiversity Conservation, Tanzania',
    description: 'Technical advisory for Biodiversity Conservation and Wetland Management in Kilombero Valley Ramsar Site.',
    tag: 'Environmental & Research',
    color: 'bg-lime-50 text-lime-700',
    tagActive: 'bg-white/20 text-white',
    fallbackIcon: 'ri-leaf-line',
  },
];

const stats = [
  { value: 50, suffix: '+', label: 'Projects Delivered' },
  { value: 15, suffix: '+', label: 'Countries Reached' },
  { value: 10, suffix: '+', label: 'Years of Trust' },
  { value: 100, suffix: '%', label: 'Client Satisfaction' },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function CountUp({ target, suffix, active }: { target: number; suffix: string; active: boolean }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [active, target]);
  return <>{count}{suffix}</>;
}

export default function Clients() {
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const { ref: sectionRef, inView } = useInView(0.1);
  const { ref: statsRef, inView: statsInView } = useInView(0.3);

  return (
    <section id="clients" className="py-24 bg-white relative overflow-hidden">

      {/* Animated floating background orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute w-[500px] h-[500px] rounded-full opacity-[0.04]"
          style={{
            background: 'radial-gradient(circle, #0d9488, transparent)',
            top: '-10%',
            right: '-5%',
            animation: 'floatOrb1 12s ease-in-out infinite',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full opacity-[0.04]"
          style={{
            background: 'radial-gradient(circle, #0d9488, transparent)',
            bottom: '5%',
            left: '-8%',
            animation: 'floatOrb2 15s ease-in-out infinite',
          }}
        />
      </div>

      <style>{`
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, 40px) scale(1.1); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(40px, -30px) scale(1.08); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(30px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .card-animate {
          opacity: 0;
        }
        .card-animate.visible {
          animation: fadeSlideIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .header-animate {
          opacity: 0;
        }
        .header-animate.visible {
          animation: fadeSlideUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6" ref={sectionRef}>

        {/* Header */}
        <div className={`text-center mb-16 header-animate ${inView ? 'visible' : ''}`}>
          <span className="text-teal-600 text-sm font-semibold tracking-widest uppercase">Our Clients</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Trusted by <span className="text-teal-600">Leading Organizations</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            We have partnered with international organizations, NGOs, and government agencies across East Africa and beyond.
          </p>
        </div>

        {/* Client Cards — staggered entrance */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client, i) => {
            const isActive = activeCard === i;
            return (
              <div
                key={client.name}
                onClick={() => setActiveCard(isActive ? null : i)}
                className={`card-animate ${inView ? 'visible' : ''} relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all duration-500 ease-in-out group
                  ${isActive ? 'bg-teal-600 scale-[1.03]' : 'bg-white border border-gray-100 hover:border-teal-200 hover:scale-[1.02]'}
                `}
                style={inView ? { animationDelay: `${i * 0.1}s` } : {}}
              >
                {/* Decorative blobs when active */}
                {isActive && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-white/10"></div>
                    <div className="absolute -top-4 -left-4 w-24 h-24 rounded-full bg-white/10"></div>
                  </div>
                )}

                {/* Shimmer line on hover */}
                <div className={`absolute top-0 left-0 right-0 h-0.5 transition-all duration-500 ${isActive ? 'bg-white/30' : 'bg-teal-400 scale-x-0 group-hover:scale-x-100 origin-left'}`}></div>

                <div className="relative flex items-start justify-between mb-5">
                  <div className="h-12 flex items-center">
                    <img
                      src={client.logo}
                      alt={`${client.name} logo`}
                      className={`max-h-10 max-w-[140px] object-contain transition-all duration-500 ${isActive ? 'brightness-0 invert' : ''}`}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className={`w-10 h-10 items-center justify-center rounded-xl hidden transition-all duration-500 ${isActive ? 'bg-white/20' : 'bg-teal-50'}`}>
                      <i className={`${client.fallbackIcon} text-xl transition-colors duration-500 ${isActive ? 'text-white' : 'text-teal-600'}`}></i>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap transition-all duration-500 ${isActive ? client.tagActive : client.color}`}>
                    {client.tag}
                  </span>
                </div>

                <h4 className={`relative font-bold text-lg mb-1 transition-colors duration-500 ${isActive ? 'text-white' : 'text-gray-900'}`}>
                  {client.name}
                </h4>
                <p className={`relative text-sm font-medium mb-3 transition-colors duration-500 ${isActive ? 'text-teal-200' : 'text-teal-600'}`}>
                  {client.project}
                </p>
                <p className={`relative text-sm leading-relaxed transition-colors duration-500 ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                  {client.description}
                </p>

                <div className={`relative mt-5 flex items-center gap-2 text-xs font-semibold tracking-wide transition-all duration-500 ${isActive ? 'text-white/70' : 'text-teal-500 opacity-0 group-hover:opacity-100 translate-x-0 group-hover:translate-x-1'}`}>
                  {isActive ? (
                    <><i className="ri-checkbox-circle-fill text-sm"></i>Selected</>
                  ) : (
                    <><i className="ri-arrow-right-line text-sm"></i>View project</>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Animated Stats Bar */}
        <div
          ref={statsRef}
          className={`mt-16 rounded-2xl bg-teal-600 p-8 grid grid-cols-2 lg:grid-cols-4 gap-6 header-animate ${statsInView ? 'visible' : ''}`}
          style={{ animationDelay: '0.2s' }}
        >
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-4xl font-black text-white mb-1">
                <CountUp target={s.value} suffix={s.suffix} active={statsInView} />
              </div>
              <div className="text-teal-200 text-sm font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        <div
          className={`mt-8 bg-teal-950 rounded-3xl p-10 md:p-16 relative overflow-hidden header-animate ${statsInView ? 'visible' : ''}`}
          style={{ animationDelay: '0.4s' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-800/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-800/20 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <div className="text-teal-400 text-6xl font-serif leading-none mb-4">&ldquo;</div>
            <p className="text-white text-xl md:text-2xl leading-relaxed font-light mb-8">
              GeoEng Systems delivered exceptional GIS accuracy and IT implementation that transformed our data collection and monitoring capabilities. Their multi-disciplinary expertise is unmatched in the region.
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-700">
                <i className="ri-user-line text-teal-300 text-xl"></i>
              </div>
              <div className="text-left">
                <div className="text-white font-semibold">Regional Programme Director</div>
                <div className="text-teal-400 text-sm">International Development Organization, East Africa</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
